import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { SignIn, useUser } from '@clerk/clerk-react'
import { Toaster, toast } from 'sonner'
import { AuthSetup } from './components/AuthSetup'
import AdminPage from './pages/AdminPage'
import BrowsePage from './pages/BrowsePage'
import HomePage from './pages/HomePage'
import UploadPage from './pages/UploadPage'
import BatchUploadPage from './pages/BatchUploadPage'
import LogoutPage from './pages/LogoutPage'
import NotFoundPage from './pages/NotFoundPage'
import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import { useRole } from './lib/roles'
import { Loader2 } from 'lucide-react'

function SignInPage() {
  const { isSignedIn, isLoaded } = useUser()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      // Get the redirect URL from query params
      const params = new URLSearchParams(location.search)
      const redirectUrl = params.get('redirect_url')

      // Redirect to the original page or home
      navigate(redirectUrl || '/', { replace: true })
    }
  }, [isSignedIn, isLoaded, navigate, location])

  if (isSignedIn) {
    return null
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <SignIn routing="path" path="/sign-in" />
    </div>
  )
}

function ProtectedRoute({ children, requireAdmin = false }: { children: React.ReactNode; requireAdmin?: boolean }) {
  const { isSignedIn, isLoaded } = useUser()
  const { isAdmin } = useRole()
  const navigate = useNavigate()
  const location = useLocation()
  const hasShownToast = useRef(false)

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      // Save the current path to redirect back after sign-in
      const returnTo = location.pathname + location.search
      navigate(`/sign-in?redirect_url=${encodeURIComponent(returnTo)}`)
    } else if (isLoaded && isSignedIn && requireAdmin && !isAdmin()) {
      // User is signed in but doesn't have admin role - show toast once
      if (!hasShownToast.current) {
        toast.error('访问受限：此页面仅限管理员访问')
        hasShownToast.current = true
      }
    }
  }, [isSignedIn, isLoaded, requireAdmin, isAdmin, navigate, location])

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!isSignedIn) {
    return null
  }

  if (requireAdmin && !isAdmin()) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>

          <h1 className="text-3xl font-bold mb-2 text-foreground">访问受限</h1>
          <h2 className="text-xl font-semibold mb-4 text-muted-foreground">403 Forbidden</h2>

          <p className="text-muted-foreground mb-8">
            您没有权限访问此页面。此页面仅限管理员访问。
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 border border-border rounded-md hover:bg-accent transition-colors"
            >
              返回上一页
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              返回首页
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

function App() {
  return (
    <AuthSetup>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/browse" element={<BrowsePage />} />

          {/* Sign in page */}
          <Route path="/sign-in/*" element={<SignInPage />} />

          {/* Logout page */}
          <Route path="/logout" element={<LogoutPage />} />

          {/* Protected routes - require admin role */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload"
            element={
              <ProtectedRoute requireAdmin>
                <UploadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/batch-upload"
            element={
              <ProtectedRoute requireAdmin>
                <BatchUploadPage />
              </ProtectedRoute>
            }
          />

          {/* 404 Not Found - Catch all route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthSetup>
  )
}

export default App
